import React, { useState, useEffect, useRef } from 'react';
import { api, createWebSocketUrl } from '../../utils/api';

const rejectReasonRef = React.useRef<HTMLTextAreaElement>(null);

interface Submission {
  id: string;
  userId: string;
  ffId: string;
  screenshotUrl: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

export function AdminSubmissions() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    fetchSubmissions();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const data = await api.getPendingSubmissions();
      setSubmissions(data.submissions);
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    const wsUrl = createWebSocketUrl(undefined, true);
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('Admin WebSocket connected');
      wsRef.current?.send(JSON.stringify({ type: 'subscribe', payload: { all: true } }));
    };

    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if ((message.type === 'new_submission' || message.type === 'submission_update') && message.payload) {
          handleRealtimeUpdate(message.payload, message.type);
        }
      } catch (e) {
        console.error('WS message parse error:', e);
      }
    };

    wsRef.current.onclose = () => {
      console.log('Admin WebSocket disconnected, reconnecting...');
      setTimeout(connectWebSocket, 3000);
    };
  };

  const handleRealtimeUpdate = (updatedSubmission: Submission, type: string) => {
    setSubmissions(prev => {
      if (type === 'new_submission') {
        if (prev.find(s => s.id === updatedSubmission.id)) return prev;
        return [updatedSubmission, ...prev];
      }
      return prev.map(s => s.id === updatedSubmission.id ? updatedSubmission : s);
    });
  };

  const handleApprove = async (submissionId: string) => {
    setActionLoading(submissionId);
    try {
      await api.adminAction(submissionId, 'approve');
    } catch (err: any) {
      alert(err.message || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (submissionId: string, reason: string) => {
    setActionLoading(submissionId);
    try {
      await api.adminAction(submissionId, 'reject', reason);
      setSelectedSubmission(null);
    } catch (err: any) {
      alert(err.message || 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (submission: Submission) => {
    setSelectedSubmission(submission);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div>
      <header style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: '800', color: 'var(--text)' }}>Submissions</h1>
        <p style={{ color: 'var(--text-muted)' }}>Review and manage pending submissions</p>
      </header>

      {error && (
        <div style={{ padding: '1rem', background: 'var(--error-light)', border: '1px solid var(--error)', borderRadius: 'var(--radius)', color: 'var(--error)', marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner" style={{ margin: '0 auto 1rem' }} />
            <p style={{ color: 'var(--text-muted)' }}>Loading submissions...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <p>No pending submissions</p>
            <p style={{ fontSize: '0.875rem' }}>All caught up!</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>FF ID</th>
                  <th>Screenshot</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => (
                  <tr key={submission.id}>
                    <td>
                      <div style={{ fontWeight: '600', fontFamily: 'monospace' }}>{submission.ffId}</div>
                    </td>
                    <td>
                      <img 
                        src={submission.screenshotUrl} 
                        alt={`Screenshot for ${submission.ffId}`}
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer' }}
                        onClick={() => window.open(submission.screenshotUrl, '_blank')}
                      />
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      {formatDate(submission.createdAt)}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleApprove(submission.id)}
                          disabled={actionLoading === submission.id}
                          className="btn btn-success"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          {actionLoading === submission.id ? (
                            <span className="spinner" style={{ width: '16px', height: '16px' }} />
                          ) : (
                            'Approve'
                          )}
                        </button>
                        <button
                          onClick={() => openRejectModal(submission)}
                          disabled={actionLoading === submission.id}
                          className="btn btn-error"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedSubmission && (
        <div className="modal-overlay" onClick={() => setSelectedSubmission(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Reject Submission</h3>
              <button className="modal-close" onClick={() => setSelectedSubmission(null)}>×</button>
            </div>
            <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
              FF ID: <strong>{selectedSubmission.ffId}</strong>
            </p>
            <img 
              src={selectedSubmission.screenshotUrl} 
              alt="Screenshot"
              className="preview-image"
              style={{ marginBottom: '1.5rem' }}
            />
            <div className="form-group">
              <label className="form-label">Rejection Reason (required)</label>
              <textarea
                id="rejectionReason"
                rows={4}
                placeholder="Enter reason for rejection..."
                ref={rejectReasonRef}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedSubmission(null)} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={() => {
                  const reason = rejectReasonRef.current?.value?.trim();
                  if (reason) {
                    handleReject(selectedSubmission.id, reason);
                  }
                }}
                disabled={actionLoading === selectedSubmission.id}
                className="btn btn-error"
              >
                {actionLoading === selectedSubmission.id ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}